/**
 * The Command Desk's `Actual Appointments — Completed` — RUN 82, spec 01 §8b.
 *
 * Authority: the Technical Delegate's Command Desk addendum to the Run 82 work order, mockup
 * approved on 17 September 2026 and REVISED the same day on his correction: *"a per-official
 * SUMMARY TALLY, not a per-match list"*. It sits last in the left column, under Tomorrow
 * Onwards, where Cards Listing used to be (§8a moved that to the rail).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT IS A READ OF
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The §A11 lock layer, and nothing else. Once a day's TO, SJ and TJ are entered in TMS — by
 * hand for Day 1 under ruling #14, by the push once it exists — the Technical Delegate marks the
 * day locked on TO Appointments, and ruling #15 makes a locked appointment the record of who
 * officiated. This panel tallies that record for the matches already played: one row per
 * official, how many they worked as TO, SJ and TJ. **Drafts are never counted**, and neither is
 * a crew that is signed off but not locked, or a match reopened with `Unlock`.
 *
 * It is fed from the schedule-wide `GET /appointments` the desk already makes for §10 — every
 * fixture with its slots and their `lockedAt` — with `GET /fixtures` supplying each match's
 * status. No read is added, and every figure is derived in `derive.ts`'s `lockedRecord`, so the
 * table and the strip above it are two views of one computation and cannot disagree.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * "COMPLETED" IS NOT THE SCHEDULE'S STATUS ALONE
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * The fixture status this system mirrors is not moved during play, so a panel that waited for
 * `completed` would never fill. A match is played once TMS says so OR the tournament clock is
 * past its kick-off plus `MATCH_LENGTH_MINUTES` — the rule the Fixtures page's `Debrief ›` link
 * already applies, through the same helper. See `matchCompleted`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RUN 82C — WHAT THE INDEPENDENT QA OF THIS PANEL CHANGED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   · **One played rule.** `matchCompleted` is now the rule the whole desk reads, so the dateline's
 *     `Played` and §10's `completed` count the same matches this tally does — and a match TMS
 *     still calls `live` is held open until kick-off plus 150 minutes, here as everywhere.
 *   · **It is read again.** The record was the lock state at page load, however long the desk
 *     stayed open. The page now re-reads it every five minutes and whenever the tab comes back
 *     into view, keeping the last good answer on screen meanwhile, and the strip ends `As Of
 *     HH:MM` — when that answer arrived, on the tournament clock.
 *   · **The empty state leads with what can be done.** Played matches that are not locked come
 *     before locked matches still to be played.
 *   · **Accessibility.** An official's name heads its row (`<th scope="row">`), and a refused read
 *     is a `status` here, because §10's Tech Table banner already announces the same refusal as
 *     an `alert` — two interruptions for one failure is one too many.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * RUN 82G — WHAT THE SECOND INDEPENDENT QA CHANGED
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *   · **The empty state is dated too (L2).** With nothing played there is no strip, and so, until
 *     now, no `As Of` — on exactly the morning the Technical Delegate wants to know whether the
 *     lock he made last night has been read. The stamp stands under the empty state whenever the
 *     strip is not drawn, and says `Refresh Failing` once the read under it is ten minutes old.
 *   · **Only a signed-off match is called ready to lock (L6).** `Mark Day Locked` settles a crew
 *     that is signed off and nothing else, so the empty state asks him to lock exactly those —
 *     `its day` for one — and names the rest as not signed off.
 *   · **The live hold is said (L6).** The rule text and the foot name the 150 minutes a match TMS
 *     still shows live is held open for, which the panel has counted by since RUN 82C.
 *
 * **RUN 82J (the third independent QA, 2026-09-18) — the lock's own condition is said.** `Mark Day
 * Locked` refuses a whole day while any crew on it is unsettled, so `mark its day locked` now adds
 * `once every crew that day is signed off`.
 *
 * **Fictional data only** wherever this is exercised; it renders the rows it is handed.
 */

import { Link } from "react-router-dom";

import { MATCH_LENGTH_MINUTES } from "@hockeytd/shared";

import { EmptyState, FailBanner, Skeleton } from "../../app/states.js";
import { AsOfStamp } from "./AsOfStamp.js";
import { LIVE_STATUS_HOLD_MINUTES, type LockedRecord, type ReadStamp } from "./derive.js";

/** `1 locked match is` / `2 locked matches are` — a count with the noun it agrees with. */
function plural(count: number, one: string, many: string): string {
  return `${String(count)} ${count === 1 ? one : many}`;
}

/**
 * §8b's strip, in the mockup's words: `1 Of 2 Matchdays Pushed & Locked · 2 Matches · 6
 * Appointments On The Locked Record`.
 *
 * The ` · ` between the two halves is for a screen reader and for anything reading the text; the
 * eye gets the flex gap and the right-aligned half instead, as the mockup draws it.
 *
 * **RUN 82C — `· As Of 16:42` closes it** (`recordStamp`, in `derive.ts`): when the read the
 * record is drawn from last answered. It rides at the end of the right-hand half, so it wraps with the figures it
 * dates rather than stranding on a line of its own. `asOf` is null only before a first answer,
 * and the strip is not drawn then anyway.
 *
 * **RUN 82G.** The stamp is {@link AsOfStamp} — the same words, plus `Refresh Failing` once the
 * read is more than ten minutes old.
 */
function Strip({
  record,
  asOf,
}: {
  record: LockedRecord;
  asOf: ReadStamp | null;
}): React.JSX.Element {
  return (
    <div className="aa-strip" data-testid="actual-strip">
      <span className="aa-big">
        {record.matchdaysLocked} Of {record.matchdays}
      </span>{" "}
      <span className="aa-txt">
        {record.matchdays === 1 ? "Matchday" : "Matchdays"} Pushed &amp; Locked
      </span>
      <span className="sr-only"> · </span>
      <span className="aa-sub">
        <b>{record.matches}</b> {record.matches === 1 ? "Match" : "Matches"} ·{" "}
        <b>{record.appointments}</b> {record.appointments === 1 ? "Appointment" : "Appointments"}{" "}
        On The Locked Record
        {asOf === null ? null : (
          <>
            {" · "}
            <AsOfStamp stamp={asOf} testId="actual-asof" />
          </>
        )}
      </span>
    </div>
  );
}

/** One count cell. A zero is dimmed — to `--ink-2`, not the mockup's faded grey (see the CSS). */
function Count({ value }: { value: number }): React.JSX.Element {
  return <td className={value === 0 ? "aa-cnt cell-cnt z" : "aa-cnt cell-cnt"}>{value}</td>;
}

/**
 * The empty state, in two honest variants.
 *
 * On the morning of Day 1 nothing has been played, and the Technical Delegate may or may not
 * have locked the day already. `Nothing yet` is true either way, but only one of the two
 * sentences below tells him whether the lock he made last night is waiting for a match or was
 * never made.
 *
 * **RUN 82C — what he can act on comes first.** A played match whose crew is not locked is a job
 * for now: its day wants marking locked on TO Appointments, and until it is, that match is missing
 * from the record. A locked match still to come only has to wait. So when any played match is
 * unlocked the detail opens by counting them, before a word about the matches to come — and that
 * count replaces `No played match has its TO, SJ and TJ marked locked yet`, which it says more
 * exactly.
 *
 * **RUN 82G — only what a lock press would settle is called lockable (second QA, L6).** RUN 82C
 * counted every played match that was not locked and told the Technical Delegate to lock its day.
 * A lock press settles a signed-off crew and nothing else: a day holding a crew nobody signed off
 * is refused, and an Official match that was never signed off is left as TMS holds it. So the
 * detail now opens with the played matches that ARE signed off — `mark its day locked` for one,
 * `their day` for more — and names the others separately as not signed off, with the three reasons
 * a played match can be that: nobody appointed, a crew not yet signed off (a match reopened with
 * `Unlock` among them), or a match TMS made Official before its crew was signed off. And the rule
 * text now says what the tally has counted by since RUN 82C: a match TMS still shows live or in
 * warm-up is held open until {@link LIVE_STATUS_HOLD_MINUTES} minutes after its kick-off.
 *
 * **RUN 82J — and says when a lock press will take (third QA, the wording caveat on L6).** The lock
 * refuses the whole day if any other crew that day is unsettled (`POST /appointments/lock`), so a
 * signed-off match is lockable only once its day's other crews are signed off too, and the sentence
 * says so rather than sending him to a button that will refuse him.
 */
function emptyDetail(record: LockedRecord): string {
  const rule =
    `joins this tally once it has been played — at full time in TMS, or ${String(MATCH_LENGTH_MINUTES)} ` +
    `minutes after its scheduled kick-off (${String(LIVE_STATUS_HOLD_MINUTES)} while TMS still shows ` +
    "it live or in warm-up). Drafts and unlocked appointments are never counted here.";
  const toCome =
    record.lockedToCome > 0
      ? `${plural(record.lockedToCome, "locked match is", "locked matches are")} still to be played. ` +
        `Each ${rule}`
      : null;
  const howToLock = `A match's crew is locked on TO Appointments once the day is entered in TMS, and ${rule}`;
  const played: string[] = [];
  if (record.playedReadyToLock > 0) {
    played.push(
      `${plural(record.playedReadyToLock, "played match", "played matches")} not locked yet — mark ` +
        `${record.playedReadyToLock === 1 ? "its" : "their"} day locked on TO Appointments once ` +
        "every crew that day is signed off.",
    );
  }
  if (record.playedNotSignedOff > 0) {
    played.push(
      `${plural(record.playedNotSignedOff, "played match", "played matches")} not signed off — ` +
        "nobody appointed, a crew not yet signed off, or Official in TMS before sign-off.",
    );
  }
  if (played.length > 0) return `${played.join(" ")} ${toCome ?? howToLock}`;
  if (toCome !== null) return toCome;
  return `No played match has its TO, SJ and TJ marked locked yet. ${howToLock}`;
}

export function ActualAppointmentsPanel({
  record,
  loading,
  failure,
  asOf,
}: {
  record: LockedRecord;
  /** The schedule-wide read is still in flight. §B9: bars, never a figure. */
  loading: boolean;
  /**
   * The schedule-wide read was refused or failed. The banner is the panel's, not the page's —
   * and a `status`, not an `alert`: §10's Tech Table draws from the same read and its banner
   * announces the same failure (RUN 82C).
   */
  failure: Error | null;
  /**
   * RUN 82C — `As Of HH:MM`, when that read last answered; null before its first answer.
   * RUN 82G — with whether that answer is old enough to say the refresh is failing.
   */
  asOf: ReadStamp | null;
}): React.JSX.Element {
  return (
    <section
      className="panel panel--d3 actualappt"
      aria-labelledby="actualTitle"
      data-howto="actual"
    >
      <div className="panel__head">
        <h3 className="panel__title" id="actualTitle">
          Actual Appointments — Completed
        </h3>
        <span className="panel__note">Confirmed On TMS · Locked Record</span>
        <span className="spacer" />
        <span className="kicker">Pushed From This Control Centre</span>
      </div>

      {failure !== null ? (
        <FailBanner error={failure} role="status" />
      ) : loading ? (
        <Skeleton rows={5} />
      ) : (
        <>
          {/* A `0 Of 0` would be a measurement of nothing: with no played match there is no
              matchday to report against, so the strip waits for the first one. */}
          {record.matchdays === 0 ? null : <Strip record={record} asOf={asOf} />}
          {record.rows.length === 0 ? (
            <>
              <EmptyState title="Nothing On The Locked Record Yet" detail={emptyDetail(record)} />
              {/* RUN 82G (L2) — the empty state is a reading of the lock state too, and is dated
                  like one. Only where the strip is not drawn: where it is, its own stamp dates
                  both, and two stamps for one read would invite a comparison of nothing. */}
              {record.matchdays !== 0 || asOf === null ? null : (
                <p className="aa-emptyasof">
                  <AsOfStamp stamp={asOf} testId="actual-asof" />
                </p>
              )}
            </>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                className="mtable"
                aria-label="Completed matches actually worked by each technical panel official, by role"
              >
                <thead>
                  <tr>
                    <th scope="col">Official</th>
                    <th scope="col" className="aa-cnt">
                      TO
                    </th>
                    <th scope="col" className="aa-cnt">
                      SJ
                    </th>
                    <th scope="col" className="aa-cnt">
                      TJ
                    </th>
                    <th scope="col" className="aa-cnt">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {record.rows.map((row) => (
                    <tr key={row.tmsPersonId} data-testid={`actual-${row.tmsPersonId}`}>
                      {/* RUN 82C — the official heads the row, so a screen reader reads each
                          count as that official's TO, SJ, TJ or Total. Styled as the cell was. */}
                      <th scope="row" className="aa-off">
                        {/* The profile, exactly as every other name on the desk opens it. */}
                        <Link
                          className="rowlink"
                          to={`/officials/${encodeURIComponent(row.tmsPersonId)}`}
                        >
                          {row.name}
                        </Link>
                        <span className="aa-nat">{row.country}</span>
                      </th>
                      <Count value={row.to} />
                      <Count value={row.sj} />
                      <Count value={row.tj} />
                      <td className="aa-cnt cell-tot">{row.total}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      <div className="panel__foot">
        <b>Locked appointments only.</b> A TO, SJ or TJ counts here once its day is marked locked
        on TO Appointments — the statement that TMS holds it — and its match has been played: at
        full time in TMS, or {MATCH_LENGTH_MINUTES} minutes after the scheduled kick-off (
        {LIVE_STATUS_HOLD_MINUTES} while TMS still shows the match live or in warm-up). Drafts,
        crews signed off but not locked, and matches reopened with <b>Unlock</b> are not the
        record and are never counted. A matchday is locked when every match played on it is.
        The record is read again every five minutes, and whenever this tab or window comes back
        into use unless it was read in the last thirty seconds; <b>As Of</b> is when it last
        answered, and adds <b>Refresh Failing</b> once that is more than ten minutes ago.
      </div>
    </section>
  );
}
